/**
 * Room chatter system (Sprint 3b): keeps every lead room feeling ALIVE.
 *  - emits a stream of NPC banter (category shaped by personality + live call)
 *  - occasionally drops a RUMOR with a hidden truth the player fact-checks
 *  - when a genuine rumor resolves, it RESERVES a real price move (via the
 *    same activeNews channel the exchange market reads), so the loop pays off
 *  - trims the per-room chat ring buffer + resolves due rumors
 * The room currently on screen ticks fast; others tick slowly (memory/save calm).
 */
import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import type { ChatMsg, LeadRoomState, RoomRuntime, Rumor } from '../leadRoomState.js';
import type { InfoState, ActiveCoinNews } from '../infoState.js';
import { COINS, getCoinConfig } from '../config/coins.js';
import { listedCoinIds } from '../exchangeState.js';
import { LEAD_ROOMS, getLeadRoom } from '../config/leadRooms.js';
import { NPC_NICKS } from '../config/comments.js';
import {
  CHATTER_CONFIG, CHATTER_POOL_SIZE, RUMOR_TEMPLATES,
  chatterWeights, pickCategory, rollRumorGenuine, nickIdxFor,
} from '../config/roomChatter.js';
import { draw, type BagStore } from '../shuffleBag.js';

const ALL_IDS = COINS.map(c => c.id);

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}
function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/** Live call direction as 1/-1/0 for weighting. */
function callDir(room: RoomRuntime): 1 | -1 | 0 {
  return room.activeCall ? room.activeCall.claimedDirection : 0;
}
/** Most recent resolved outcome (drives self-referential banter). */
function lastOutcome(room: RoomRuntime): 'hit' | 'miss' | undefined {
  const last = room.history[0];
  return last ? (last.status === 'hit' ? 'hit' : 'miss') : undefined;
}

/**
 * Build one banter line for a room. The text index is drawn from a shuffle bag
 * keyed by room + category (independent cursor per room+category), so a line
 * does not repeat before that category's pool is exhausted.
 */
export function makeChatMsg(
  bags: BagStore, roomId: string, room: RoomRuntime, now: number
): { msg: ChatMsg; bags: BagStore } {
  const cfg = getLeadRoom(roomId);
  const weights = chatterWeights(cfg?.personality ?? 'honest', callDir(room), lastOutcome(room));
  const category = pickCategory(weights, Math.random());
  const id = `${roomId}_c_${now}_${Math.floor(Math.random() * 1e6)}`;
  const drawn = draw(bags, `chat_${roomId}_${category}`, CHATTER_POOL_SIZE);
  const msg: ChatMsg = {
    id,
    nickIdx: nickIdxFor(id, NPC_NICKS.length),
    category,
    textIdx: drawn.index,
    at: now,
  };
  return { msg, bags: drawn.bags };
}

/**
 * Build a rumor for a room targeting a listed coin; truth biased by personality.
 * The rumor KIND and its phrasing VARIANT are each drawn from a shuffle bag so
 * neither repeats before its pool is exhausted.
 */
export function makeRumor(
  bags: BagStore, roomId: string, now: number, listedIds: string[]
): { rumor: Rumor; bags: BagStore } {
  const cfg = getLeadRoom(roomId);
  const ids = listedIds.length > 0 ? listedIds : ALL_IDS;
  // collusion rooms whisper about meme coins (where the traps live)
  const memeIds = ids.filter(id => getCoinConfig(id)?.tier === 'meme');
  const pool = (cfg?.personality === 'collusion' && memeIds.length > 0) ? memeIds : ids;
  const coinId = pick(pool);
  const kind = draw(bags, 'rumor_kind', RUMOR_TEMPLATES.length);
  const tpl = RUMOR_TEMPLATES[kind.index];
  const variant = draw(kind.bags, `rumor_var_${tpl.id}`, tpl.variants);
  const genuine = rollRumorGenuine(cfg?.personality ?? 'honest', Math.random());
  const rumor: Rumor = {
    id: `${roomId}_r_${now}_${Math.floor(Math.random() * 1e6)}`,
    templateId: tpl.id,
    variant: variant.index + 1,
    coinId,
    direction: tpl.direction,
    genuine,
    createdAt: now,
    resolveAt: now + CHATTER_CONFIG.rumorLifetime,
    factChecked: false,
    fired: false,
  };
  return { rumor, bags: variant.bags };
}

function pushChat(room: RoomRuntime, msg: ChatMsg): RoomRuntime {
  return { ...room, chat: [...room.chat, msg].slice(-CHATTER_CONFIG.maxChat) };
}

/**
 * Reserve a real market push for a genuine rumor (synthetic activeNews entry).
 * `delay` embargoes the price move (front-running window): the effect only
 * starts after `now + delay`, so a player who confirmed the rumor early can
 * enter before the crowd. delay = 0 reflects immediately.
 */
export function reserveRumorMove(info: InfoState, rumor: Rumor, now: number, delay = 0): InfoState {
  const tpl = RUMOR_TEMPLATES.find(t => t.id === rumor.templateId);
  if (!tpl) return info;
  // guard against a duplicate reservation for the same rumor
  const key = `rumor_${rumor.id}`;
  if (info.activeNews.some(n => n.instanceId === key)) return info;
  const startsAt = now + Math.max(0, delay);
  const entry: ActiveCoinNews = {
    instanceId: key,
    newsId: 'rumor',
    coinId: rumor.coinId,
    direction: rumor.direction,
    impact: tpl.impact,
    publishedAt: startsAt,
    expiresAt: startsAt + tpl.moveDuration,
    startsAt,
  };
  return { ...info, activeNews: [...info.activeNews, entry] };
}

/** Front-run delay for a confirmed rumor (15~30s). Pure in the roll. */
export function factCheckMoveDelay(roll: number): number {
  const { factCheckMoveDelayMin, factCheckMoveDelayMax } = CHATTER_CONFIG;
  return factCheckMoveDelayMin + roll * (factCheckMoveDelayMax - factCheckMoveDelayMin);
}

/**
 * Advance one room's chatter + rumors. Returns the next room plus any info
 * mutation (genuine rumors firing their reserved market move). Pure aside from
 * Math.random. `viewed` = the on-screen room (ticks fast).
 */
export function tickChatter(
  bags: BagStore,
  roomId: string,
  room: RoomRuntime,
  info: InfoState,
  now: number,
  viewed: boolean,
  listedIds: string[] = []
): { room: RoomRuntime; info: InfoState; bags: BagStore } {
  let next = room;
  let nextInfo = info;
  let nextBags = bags;

  // 1. resolve due rumors; genuine ones fire their reserved price move once
  if (next.rumors.length > 0) {
    const stillLive: Rumor[] = [];
    for (const r of next.rumors) {
      if (now >= r.resolveAt) {
        if (r.genuine && !r.fired) nextInfo = reserveRumorMove(nextInfo, r, now);
        // resolved rumors drop out of the live list (verdict already visible)
        continue;
      }
      stillLive.push(r);
    }
    if (stillLive.length !== next.rumors.length) next = { ...next, rumors: stillLive };
  }

  // 2. a fact-checked genuine rumor SCHEDULES its move after a 15~30s embargo,
  //    giving the informed player a head start to enter before the crowd.
  if (next.rumors.some(r => r.factChecked && r.genuine && !r.fired)) {
    const rumors = next.rumors.map(r => {
      if (r.factChecked && r.genuine && !r.fired) {
        nextInfo = reserveRumorMove(nextInfo, r, now, factCheckMoveDelay(Math.random()));
        return { ...r, fired: true };
      }
      return r;
    });
    next = { ...next, rumors };
  }

  // 3. emit a chatter line / rumor when due
  if (now >= next.nextChatAt) {
    const rumorRoom = Math.random() < CHATTER_CONFIG.rumorChance
      && next.rumors.length < CHATTER_CONFIG.maxRumors;
    if (rumorRoom) {
      const r = makeRumor(nextBags, roomId, now, listedIds);
      nextBags = r.bags;
      next = { ...next, rumors: [...next.rumors, r.rumor] };
    } else {
      const c = makeChatMsg(nextBags, roomId, next, now);
      nextBags = c.bags;
      next = pushChat(next, c.msg);
    }
    const gap = randRange(CHATTER_CONFIG.emitIntervalMin, CHATTER_CONFIG.emitIntervalMax)
      * (viewed ? 1 : CHATTER_CONFIG.backgroundMultiplier);
    next = { ...next, nextChatAt: now + gap };
  }

  return { room: next, info: nextInfo, bags: nextBags };
}

/** Pre-fill a room's chat so first entry feels lived-in (idempotent per room). */
export function seedChat(
  bags: BagStore, roomId: string, room: RoomRuntime, now: number
): { room: RoomRuntime; bags: BagStore } {
  if (room.chat.length > 0) return { room, bags };
  let next = room;
  let nextBags = bags;
  const step = (CHATTER_CONFIG.emitIntervalMin + CHATTER_CONFIG.emitIntervalMax) / 2;
  for (let i = CHATTER_CONFIG.seedCount; i > 0; i--) {
    const at = now - i * step;
    const c = makeChatMsg(nextBags, roomId, next, at);
    nextBags = c.bags;
    next = pushChat(next, c.msg);
  }
  return {
    room: { ...next, nextChatAt: now + randRange(CHATTER_CONFIG.emitIntervalMin, CHATTER_CONFIG.emitIntervalMax) },
    bags: nextBags,
  };
}

/** Whether the lead-room screen is on-screen (all rooms tick fast + seed). */
let leadScreenActive = false;
export function setLeadScreenActive(active: boolean): void { leadScreenActive = active; }

export const chatterSystem: TickSystem<GameState, GameAction> = {
  name: 'roomChatter',

  update(state) {
    if (!state.isRunning || !state.leadRooms || !state.info) return state;
    const now = Date.now();
    const lead = state.leadRooms;
    let info = state.info;
    let bags = lead.bags;
    const listedIds = state.exchange ? listedCoinIds(state.exchange) : [];
    let changed = false;

    const rooms: Record<string, RoomRuntime> = {};
    for (const cfg of LEAD_ROOMS) {
      let room = lead.rooms[cfg.id];
      if (!room) { rooms[cfg.id] = room; continue; }
      // seed rooms lazily when the player opens the lead-room screen
      if (leadScreenActive && room.chat.length === 0) {
        const s = seedChat(bags, cfg.id, room, now);
        room = s.room; bags = s.bags; changed = true;
      }
      const res = tickChatter(bags, cfg.id, room, info, now, leadScreenActive, listedIds);
      if (res.room !== room) changed = true;
      if (res.info !== info) { info = res.info; changed = true; }
      if (res.bags !== bags) { bags = res.bags; changed = true; }
      rooms[cfg.id] = res.room;
    }

    if (!changed) return state;
    const nextLead: LeadRoomState = { ...lead, rooms, bags };
    return { ...state, leadRooms: nextLead, info };
  },
};
